<template>
  <article class="post-card">
    <div class="post-card__media">
      <img
        v-if="post.feature_image"
        :src="src"
        :srcset="srcset"
        :sizes="srcset ? sizes : null"
        :alt="post.feature_image_alt || ''"
        :loading="eager ? 'eager' : 'lazy'"
        :fetchpriority="eager ? 'high' : null"
        decoding="async"
        width="600"
        height="338"
      />
      <!-- Posts without artwork previously rendered a broken <img>; a plain
           tinted panel keeps the grid rows the same height. -->
      <div v-else class="post-card__media-fallback" aria-hidden="true" />
    </div>

    <div class="post-card__body">
      <p v-if="publishedLabel" class="post-card__meta">
        <time :datetime="post.published_at">{{ publishedLabel }}</time>
        <span v-if="authorName"> · {{ authorName }}</span>
      </p>

      <h2 class="post-card__title">{{ post.title }}</h2>

      <p v-if="post.excerpt" class="post-card__excerpt">{{ post.excerpt }}</p>
    </div>
  </article>
</template>

<script>
import {
  CARD_SIZES,
  ghostImageSrcset,
  ghostImageUrl,
} from '@/helpers/ghost-image'

export default {
  name: 'BlogListItem',
  props: {
    // Cards above the fold skip lazy-loading: the LCP element must not wait
    // for the loader to decide it is in view.
    eager: { type: Boolean, default: false },
    post: {
      type: Object,
      default: () => {
        return {
          title: 'Title',
          authors: [{ name: 'Authors name' }],
        }
      },
    },
  },
  computed: {
    sizes() {
      return CARD_SIZES
    },
    // Falls back to the original for non-Ghost URLs.
    src() {
      return ghostImageUrl(this.post.feature_image, 600)
    },
    srcset() {
      return ghostImageSrcset(this.post.feature_image)
    },
    authorName() {
      const authors = this.post.authors
      return authors && authors.length ? authors[0].name : ''
    },
    publishedLabel() {
      const published = this.post.published_at
      if (!published) {
        return ''
      }

      const date = new Date(published)
      if (Number.isNaN(date.getTime())) {
        return ''
      }

      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.post-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: var(--ps-white);
  border: 1px solid var(--ps-border);
  border-radius: var(--ps-radius-lg);
  box-shadow: var(--ps-shadow-sm);
  transition: border-color var(--ps-transition), box-shadow var(--ps-transition),
    transform var(--ps-transition);
}

// The whole card is wrapped in a <nuxt-link> by the listing, so hover and
// keyboard focus are driven from the anchor rather than the card itself.
a:hover .post-card {
  border-color: var(--ps-indigo-200);
  box-shadow: var(--ps-shadow-md);
  transform: translateY(-2px);
}

a:focus-visible .post-card {
  border-color: var(--ps-indigo);
  box-shadow: var(--ps-focus-ring);
}

.post-card__media {
  position: relative;
  // Fixed ratio so mixed-aspect Ghost artwork can't make the grid ragged.
  padding-top: 56.25%;
  background: var(--ps-surface);

  img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.post-card__media-fallback {
  position: absolute;
  inset: 0;
  background: var(--ps-indigo-50);
}

.post-card__body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  flex: 1;
}

.post-card__meta {
  margin: 0;
  font-size: var(--ps-text-label);
  color: var(--ps-muted);
}

.post-card__title {
  margin: 0;
  font-size: var(--ps-text-h3);
  line-height: 1.25;
  font-weight: 600;
  color: var(--ps-ink);
}

.post-card__excerpt {
  margin: 0;
  font-size: var(--ps-text-small);
  line-height: 1.55;
  color: var(--ps-body);
  // Three lines keeps every card in a row the same height without JS.
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
